import axios from 'axios';
import type {
    UserRegisterRequest,
    UserUpdatePasswordRequest,
    UserUpdatePasswordResponse,
    SuccessResponse,
    LoginResponse,
} from '@/api/types/Auth';
import type { Resource } from '@/api/model/Resource';
import type { UserJson } from '@/api/model/User';
import { User } from '@/api/model/User';

function ensureCsrfCookie(): Promise<void> {
    return axios.get<void>('/csrf-cookie').then(() => undefined);
}

export default function useAuthService() {
    return {
        getCurrentUser(): Promise<User> {
            return axios.get<Resource<UserJson>>(`/api/users/me`).then((response) => User.fromJSON(response.data.data));
        },

        async login(email: string, password: string): Promise<LoginResponse> {
            await ensureCsrfCookie();
            const response = await axios.post<LoginResponse>(`/api/fortify/login`, {
                email: email,
                password: password,
            });
            // Fetch a fresh CSRF cookie via GET so that Passport's
            // CreateFreshApiToken middleware attaches the laravel_token
            // JWT cookie needed for subsequent API requests.
            await ensureCsrfCookie();
            return response.data;
        },

        async register(credentials: UserRegisterRequest): Promise<void> {
            await ensureCsrfCookie();
            await axios.post<void>(`/api/fortify/register`, credentials);
            await ensureCsrfCookie();
        },

        async logout(): Promise<void> {
            return axios.post<void>('/api/fortify/logout').then((response) => response.data);
        },

        async createPassword(createData: UserUpdatePasswordRequest): Promise<number> {
            await ensureCsrfCookie();
            const { status } = await axios.post<UserUpdatePasswordResponse>('/api/signup/create-password', createData);
            return status;
        },

        async forgotPassword(email: string): Promise<SuccessResponse> {
            await ensureCsrfCookie();
            const response = await axios.post<SuccessResponse>('/api/fortify/forgot-password', {
                email: email,
            });
            return response.data;
        },

        async resetPassword(
            email: string,
            token: string,
            password: string,
            password_confirmation: string,
        ): Promise<SuccessResponse> {
            await ensureCsrfCookie();
            const response = await axios.post<SuccessResponse>('/api/fortify/reset-password', {
                email: email,
                token: token,
                password: password,
                password_confirmation: password_confirmation,
            });
            return response.data;
        },

        async requestEmailVerificationLink(email: string): Promise<SuccessResponse> {
            await ensureCsrfCookie();
            const response = await axios.post<SuccessResponse>('/api/fortify/email/verification-notification', {
                email: email,
            });
            return response.data;
        },
    };
}
